import { isMicrosoft365WriteTool, microsoft365ToolOf, type ApprovalModeDto, type BackgroundTaskDto, type SentArgumentsDto } from '@dbrain/shared';
import { Badge, Button, ConfirmDialog, Icons, Progress, RadioCard, Select, TextArea } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';
import type { ApprovalView, ChatItem, HelperInfo, TaskView, TodoView } from '@dbrain/app-core/conversation-state';
import { fieldCount, sentConnection, sentIntro, sentView, type SentIntro, type SentRow, type SentValue } from '@dbrain/app-core/sent-arguments';
import { ErrorAlert } from '../ErrorAlert.js';
import { Microsoft365ApprovalCard } from '../microsoft365/write/WriteCard.js';
import { Markdown } from './Markdown.js';

/**
 * Agent parts of a conversation (Phase 9): approval, question and plan
 * cards with "Explain" (D-25, QW-405, D-07), the task list (D-19), helpers
 * with their cost (QW-809), background tasks (SA-020), the mode selector
 * (D-15, D-23) and notices that offer "continue" (D-01, QW-208).
 */
export const stateKey = (conversationId: string) => ['conversation', conversationId, 'state'] as const;

function useDecision(approval: ApprovalView) {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api('POST', `/api/approvals/${approval.id}`, body),
  });
}

/** D-25: a separate, tool-free explanation in the user's language; cached on the server. */
function Explain({ approvalId }: { approvalId: string }) {
  const { t } = useTranslation();
  const explain = useMutation({ mutationFn: () => api<{ explanation: string }>('POST', `/api/approvals/${approvalId}/explain`) });
  if (explain.data) {
    return (
      <p className="approval-card__explanation" data-testid="approval-explanation">
        {explain.data.explanation}
      </p>
    );
  }
  return (
    <>
      <ErrorAlert error={explain.error} />
      <Button size="sm" variant="secondary" className="approval-card__explain" busy={explain.isPending} onClick={() => explain.mutate()} data-testid="approval-explain">
        {t('agent.approval.explain')}
      </Button>
    </>
  );
}

function NotifyButton() {
  const { t } = useTranslation();
  const [permission, setPermission] = useState(typeof Notification === 'undefined' ? 'denied' : Notification.permission);
  if (permission !== 'default') return null;
  return (
    <Button size="sm" variant="ghost" onClick={() => void Notification.requestPermission().then(setPermission)}>
      {t('agent.notify.enable')}
    </Button>
  );
}

/** D-178: one value of what is sent: texts fold when long, yes/no in words, lists as lists, nested blocks folded. */
function SentValueView({ value }: { value: SentValue }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  switch (value.kind) {
    case 'empty':
      return <span className="sent__empty">{t('agent.sent.empty')}</span>;
    case 'bool':
      return <span>{t(value.value ? 'common.yes' : 'common.no')}</span>;
    case 'number':
      return <span>{value.text}</span>;
    case 'text':
      return (
        <>
          <span className={`sent__text${value.long && !open ? ' sent__text--folded' : ''}`}>{value.text}</span>
          {value.cut !== null ? <span className="sent__cut">{t('agent.sent.cut', { count: value.cut, chars: value.cut.toLocaleString(i18n.language) })}</span> : null}
          {value.long ? (
            <button type="button" className="sent__more" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="sent-more">
              {open ? t('agent.sent.less') : t('agent.sent.more')}
            </button>
          ) : null}
        </>
      );
    case 'list':
      return (
        <ul className="sent__list">
          {value.items.map((item, index) => (
            <li key={index}>
              <SentValueView value={item} />
            </li>
          ))}
        </ul>
      );
    case 'object':
      return (
        <details className="sent__block">
          <summary>{t('agent.sent.fields', { count: fieldCount(value) })}</summary>
          <SentRows rows={value.rows} />
        </details>
      );
  }
}

function SentRows({ rows }: { rows: SentRow[] }) {
  return (
    <dl className="sent__rows">
      {rows.map((row) => (
        <div key={row.key} className="sent__row" data-testid="sent-row">
          <dt>{row.label}</dt>
          <dd>
            <SentValueView value={row.value} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** D-178 (OnayKartiAyrinti.dc.html): everything a connection's tool sends (or one of Oppy's own tools gets), in rows, with the raw JSON a click away. */
function SentArguments({ sent }: { sent: SentArgumentsDto }) {
  const { t, i18n } = useTranslation();
  const view = sentView(sent);
  return (
    <div className="sent" data-testid="approval-sent">
      {view.truncated ? (
        <p className="sent__truncated" role="note" data-testid="sent-truncated">
          {t('agent.sent.truncated', { shown: view.truncated.shown.toLocaleString(i18n.language), total: view.truncated.total.toLocaleString(i18n.language) })}
        </p>
      ) : null}
      {view.rows.length > 0 ? <SentRows rows={view.rows} /> : null}
      <details className="sent__raw">
        <summary data-testid="sent-raw">
          <Icons.code size={14} />
          {t('agent.sent.raw')}
        </summary>
        <pre className="code-block">{view.raw}</pre>
      </details>
    </div>
  );
}

/** The words over a card's input: a connection's tool (or the web) gets it, `remember` keeps it, another of Oppy's tools uses it. */
const SENT_INTRO: Record<SentIntro, string> = { send: 'agent.sent.intro', remember: 'agent.sent.introRemember', use: 'agent.sent.introOwn' };

function ToolApproval({ approval }: { approval: ApprovalView }) {
  const { t } = useTranslation();
  const decide = useDecision(approval);
  const firstAllow = approval.options.find((o) => o.kind === 'allow')?.id;
  const lines = approval.details?.split('\n') ?? [];
  // only a connection's tool names where its input goes; Oppy's own tools (D-199: remember) show the input alone
  const connection = sentConnection(approval);
  return (
    <div className="approval-card" role="group" aria-label={t('agent.approval.waiting')} data-testid="approval-card" data-kind="tool" data-locked={approval.locked || undefined}>
      {approval.subagent ? <p className="approval-card__from muted">{t('agent.approval.fromHelper', { description: approval.subagent.description })}</p> : null}
      <div className="approval-card__head">
        <Icons.warning />
        <strong className="approval-card__title">{approval.title}</strong>
        {connection ? (
          <span className="approval-card__source" data-testid="approval-source">
            {t('agent.sent.source', { name: connection.name, tool: connection.tool })}
          </span>
        ) : null}
      </div>
      {approval.sent ? (
        <>
          <p className="approval-card__summary">
            {approval.summary ? `${approval.summary} ` : ''}
            {t(SENT_INTRO[sentIntro(approval) ?? 'use'])}
          </p>
          <SentArguments sent={approval.sent} />
        </>
      ) : (
        <>
          {approval.details ? <pre className="code-block approval-card__command">{lines.slice(0, 12).join('\n')}</pre> : null}
          {approval.summary ? <p className="approval-card__summary">{approval.summary}</p> : null}
        </>
      )}
      {approval.effects.length > 0 ? (
        <ul className="approval-card__effects">
          {approval.effects.map((effect) => (
            <li key={effect}>{effect}</li>
          ))}
        </ul>
      ) : null}
      {!approval.sent && lines.length > 12 ? (
        <details className="tool-card__details">
          <summary>{t('conversation.tool.details')}</summary>
          <pre className="code-block">{approval.details}</pre>
        </details>
      ) : null}
      <Explain approvalId={approval.id} />
      <ErrorAlert error={decide.error} />
      <div className="approval-card__actions">
        {approval.options.map((option) => (
          <Button
            key={option.id}
            size="sm"
            variant={option.id === firstAllow ? 'primary' : option.kind === 'reject' ? 'danger' : 'secondary'}
            busy={decide.isPending && decide.variables?.option === option.id}
            disabled={decide.isPending}
            onClick={() => decide.mutate({ option: option.id })}
            data-testid={`approval-option-${option.id}`}
          >
            {option.label}
          </Button>
        ))}
        <NotifyButton />
      </div>
    </div>
  );
}

/**
 * QW-405, D-29: multiple choice, several questions, own words; it times out
 * (docs/design/mockups/SoruKarti.dc.html). The options are cards; "Atla"
 * and "Yanıtla" stay in view at the bottom of the card while it is on screen.
 */
function QuestionCard({ approval }: { approval: ApprovalView }) {
  const { t, i18n } = useTranslation();
  const decide = useDecision(approval);
  const titleId = useId();
  const [answers, setAnswers] = useState<Record<number, { selected: string[]; other: string }>>({});
  const answerOf = (index: number) => answers[index] ?? { selected: [], other: '' };
  const set = (index: number, change: Partial<{ selected: string[]; other: string }>) => setAnswers((current) => ({ ...current, [index]: { ...answerOf(index), ...change } }));
  const submit = () => {
    const body: Record<string, string> = {};
    approval.questions.forEach((_q, index) => {
      const answer = answerOf(index);
      const parts = [...answer.selected, answer.other.trim()].filter(Boolean);
      if (parts.length > 0) body[String(index)] = parts.join(', ');
    });
    decide.mutate({ option: 'submit', answers: body });
  };
  const count = approval.questions.length;
  return (
    <section className="question-card" aria-labelledby={titleId} data-testid="question-card">
      <div className="question-card__head">
        <h2 id={titleId} className="question-card__title">
          {t('agent.question.heading', { count })}
        </h2>
        <p className="question-card__intro">{t('agent.question.intro')}</p>
      </div>
      {approval.questions.map((question, index) => (
        <fieldset key={index} className="question">
          <legend className="question__legend">
            <span className="question__header">{question.header ? `${index + 1} · ${question.header}` : `${index + 1}`}</span>
            <span className="question__text">{question.question}</span>
          </legend>
          <div className={`question__options question__options--${question.options.length > 2 ? 3 : 2}`}>
            {question.options.map((option) => {
              const checked = answerOf(index).selected.includes(option.label);
              return (
                <RadioCard
                  key={option.label}
                  multiple={Boolean(question.multiSelect)}
                  name={`q-${approval.id}-${index}`}
                  checked={checked}
                  onChange={() =>
                    set(index, {
                      selected: question.multiSelect ? (checked ? answerOf(index).selected.filter((s) => s !== option.label) : [...answerOf(index).selected, option.label]) : [option.label],
                    })
                  }
                  title={option.label}
                  hint={option.description}
                  data-testid="question-option"
                />
              );
            })}
          </div>
          <label className="question__other">
            <Icons.edit size={15} />
            <span className="ui-visually-hidden">{t('agent.question.other')}</span>
            <input value={answerOf(index).other} onChange={(e) => set(index, { other: e.target.value })} placeholder={t('agent.question.otherPlaceholder')} data-testid="question-other" />
          </label>
        </fieldset>
      ))}
      <ErrorAlert error={decide.error} />
      <div className="question-card__foot">
        <span className="question-card__timeout">
          {approval.expiresAt ? (
            <>
              <Icons.time size={15} />
              {t('agent.question.expires', { time: new Date(approval.expiresAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) })}
            </>
          ) : null}
        </span>
        <NotifyButton />
        <Button variant="ghost" disabled={decide.isPending} onClick={() => decide.mutate({ option: 'ignore' })} data-testid="question-skip">
          {t('agent.question.skip')}
        </Button>
        <Button busy={decide.isPending} onClick={submit} data-testid="question-submit">
          {t('agent.question.submit')}
        </Button>
      </div>
    </section>
  );
}

/** D-07, QW-510: the plan formatted, editable, with a note for "keep planning". */
function PlanCard({ approval }: { approval: ApprovalView }) {
  const { t } = useTranslation();
  const decide = useDecision(approval);
  const original = approval.plan ?? '';
  const [draft, setDraft] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  return (
    <div className="approval-card approval-card--plan" data-testid="plan-card">
      <strong className="approval-card__title">{approval.title}</strong>
      {approval.summary ? <p className="approval-card__summary muted">{approval.summary}</p> : null}
      {draft === null ? (
        <Markdown source={original} className="plan" />
      ) : (
        <TextArea aria-label={t('agent.plan.edit')} value={draft} rows={12} onChange={(e) => setDraft(e.target.value)} data-testid="plan-editor" />
      )}
      <div className="row">
        <Button size="sm" variant="secondary" onClick={() => setDraft(draft === null ? original : null)} data-testid="plan-edit">
          {draft === null ? t('agent.plan.edit') : t('agent.plan.preview')}
        </Button>
      </div>
      <TextArea aria-label={t('agent.plan.feedback')} placeholder={t('agent.plan.feedback')} value={feedback} rows={2} onChange={(e) => setFeedback(e.target.value)} />
      <Explain approvalId={approval.id} />
      <ErrorAlert error={decide.error} />
      <div className="approval-card__actions">
        {approval.options.map((option, index) => (
          <Button
            key={option.id}
            size="sm"
            variant={index === 0 ? 'primary' : option.kind === 'reject' ? 'ghost' : 'secondary'}
            disabled={decide.isPending}
            busy={decide.isPending && decide.variables?.option === option.id}
            onClick={() =>
              decide.mutate(
                option.kind === 'reject'
                  ? { option: option.id, ...(feedback.trim() ? { feedback: feedback.trim() } : {}) }
                  : { option: option.id, ...(draft !== null && draft.trim() !== original.trim() ? { plan: draft } : {}) },
              )
            }
            data-testid={`plan-option-${option.id}`}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function ResolvedApproval({ approval }: { approval: ApprovalView }) {
  const { t } = useTranslation();
  // an approved plan is shown by the plan block that follows
  if (approval.kind === 'plan' && approval.status === 'approved') return null;
  const label = approval.options.find((o) => o.id === approval.option)?.label ?? approval.option ?? '';
  const title = approval.kind === 'plan' ? t('agent.plan.title') : approval.kind === 'question' ? t('agent.question.title') : approval.title;
  const text =
    approval.status === 'approved'
      ? approval.kind === 'question'
        ? t('agent.approval.answered')
        : t('agent.approval.approved', { option: label })
      : approval.kind === 'plan' && approval.status === 'rejected'
        ? t('agent.plan.keptPlanning')
        : t(`agent.approval.${approval.status}`);
  return (
    <p className="approval-resolved" data-testid="approval-resolved" data-status={approval.status}>
      {approval.status === 'approved' ? <Icons.done size={16} /> : <Icons.skipped size={16} />}
      <span>
        {title} — {text}
      </span>
    </p>
  );
}

/** D-178: the arguments and details show in both profiles; the user sees what will be sent before saying yes. */
export function ApprovalCard({ approval }: { approval: ApprovalView; profile?: 'chat' | 'code' }) {
  // Microsoft 365 part 3: a mail or meeting card of its own, waiting or decided (it stays in the flow with its state)
  if (approval.microsoft365 || isMicrosoft365WriteTool(microsoft365ToolOf(approval.tool))) return <Microsoft365ApprovalCard approval={approval} />;
  if (approval.status !== 'pending') return <ResolvedApproval approval={approval} />;
  if (approval.kind === 'question') return <QuestionCard approval={approval} />;
  if (approval.kind === 'plan') return <PlanCard approval={approval} />;
  return <ToolApproval approval={approval} />;
}

export function ApprovedPlan({ item }: { item: Extract<ChatItem, { kind: 'plan' }> }) {
  const { t } = useTranslation();
  return (
    <div className="plan-approved" data-testid="plan-approved">
      <Badge tone="success">{item.edited ? t('agent.plan.approvedEdited') : t('agent.plan.approved')}</Badge>
      <Markdown source={item.plan} className="plan" />
    </div>
  );
}

/** D-19, QW-805: the task list stays above the composer while the agent works. */
export function TodoPanel({ todos }: { todos: TodoView[] }) {
  const { t } = useTranslation();
  if (todos.length === 0) return null;
  const done = todos.filter((item) => item.status === 'completed').length;
  // QW-1305: "step i / n" while the agent works through the list
  const active = todos.findIndex((item) => item.status === 'in_progress');
  const step = Math.min(todos.length, active >= 0 ? active + 1 : done + 1);
  return (
    <details className="todo-panel" open={done < todos.length} data-testid="todo-panel">
      <summary>
        {t('agent.todos.title', { done, total: todos.length })}
        {done < todos.length ? <span className="todo-panel__step" data-testid="todo-step"> · {t('agent.todos.step', { current: step, total: todos.length })}</span> : null}
      </summary>
      <div className="todo-panel__progress">
        <Progress value={done} max={todos.length} label={t('agent.todos.progress')} tone="success" />
      </div>
      <ol className="todo-list">
        {todos.map((item) => (
          <li key={item.id} className={`todo todo--${item.status}`} data-status={item.status}>
            {item.status === 'completed' ? (
              <Icons.done size={16} />
            ) : item.status === 'in_progress' ? (
              <span className="ui-spinner" aria-hidden="true" />
            ) : (
              <Icons.pending size={16} />
            )}
            <span>
              {item.content}
              {item.blockedBy && item.blockedBy.length > 0 ? <span className="muted"> {t('agent.todos.waits', { ids: item.blockedBy.join(', ') })}</span> : null}
            </span>
            {item.status === 'in_progress' ? <span className="todo__state">{t('agent.todos.running')}</span> : null}
          </li>
        ))}
      </ol>
    </details>
  );
}

function helperTone(status: HelperInfo['status']) {
  return status === 'completed' ? 'success' : status === 'running' ? 'accent' : 'warning';
}

/** QW-806, QW-809: helpers of one reply in one group, each with its cost. */
export function HelperGroup({ items }: { items: Extract<ChatItem, { kind: 'tool' }>[] }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="helper-group" data-testid="helper-group">
      {items.length > 1 ? <span className="muted">{t('agent.helper.group', { count: items.length })}</span> : null}
      {items.map((item) => {
        const helper = item.helper;
        const status = helper?.status ?? (item.status === 'failed' ? 'failed' : 'running');
        return (
          <div key={item.callId} className="helper-card" data-testid="helper-card" data-status={status}>
            <div className="tool-card__head">
              <Badge tone={helperTone(status)}>{t(`agent.helper.status.${status}`)}</Badge>
              <strong>{helper?.description ?? item.title}</strong>
              {helper?.background ? <Badge>{t('agent.helper.background')}</Badge> : null}
            </div>
            {helper?.usage ? (
              <p className="muted helper-card__usage" data-testid="helper-usage">
                {t('agent.helper.usage', {
                  tokens: (helper.usage.inputTokens + helper.usage.outputTokens).toLocaleString(i18n.language),
                  calls: helper.usage.modelCalls,
                  tools: helper.usage.toolCalls ?? 0,
                  seconds: Math.round((helper.usage.durationMs ?? 0) / 1000),
                })}
                {helper.usage.costMicros > 0 ? ` · ${t('agent.helper.cost', { cost: (helper.usage.costMicros / 1_000_000).toFixed(4) })}` : ''}
              </p>
            ) : null}
            {helper && helper.steps.length > 0 ? (
              <details className="tool-card__details">
                <summary>{t('agent.helper.steps', { count: helper.steps.length })}</summary>
                <ul className="helper-steps">
                  {helper.steps.map((step) => (
                    <li key={step.callId} data-status={step.status}>
                      {step.title}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
            {item.result || helper?.lastText ? (
              <details className="tool-card__details">
                <summary>{t('agent.helper.result')}</summary>
                <p className="message__text">{item.result ?? helper?.lastText}</p>
              </details>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** SA-020, QW-709: background work of this conversation, with stop. */
export function TasksPanel({ conversationId, tasks }: { conversationId: string; tasks: (TaskView | BackgroundTaskDto)[] }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const stop = useMutation({
    mutationFn: (taskId: string) => api('POST', `/api/tasks/${taskId}/stop`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: stateKey(conversationId) }),
  });
  if (tasks.length === 0) return null;
  const running = tasks.filter((task) => task.status === 'running').length;
  return (
    <details className="tasks-panel" open={running > 0} data-testid="tasks-panel">
      <summary>{t('agent.tasks.title', { running, total: tasks.length })}</summary>
      <ErrorAlert error={stop.error} />
      <ul className="file-list">
        {tasks.map((task) => (
          <li key={task.id} data-status={task.status}>
            <span>
              <Badge tone={task.status === 'running' ? 'accent' : task.status === 'completed' ? 'success' : 'warning'}>{t(`agent.tasks.status.${task.status}`)}</Badge> {task.label}
            </span>
            {task.status === 'running' ? (
              <Button size="sm" variant="secondary" busy={stop.isPending && stop.variables === task.id} onClick={() => stop.mutate(task.id)} data-testid="task-stop">
                {t('agent.tasks.stop')}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </details>
  );
}

/** D-15, D-23: Ask by default; the modes this conversation may use. */
export function ModeSelector({ conversationId, mode, available, compact = false }: { conversationId: string; mode: ApprovalModeDto; available: ApprovalModeDto[]; compact?: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const change = useMutation({
    mutationFn: (next: ApprovalModeDto) => api('PATCH', `/api/conversations/${conversationId}/mode`, { mode: next }),
    onSettled: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: stateKey(conversationId) });
    },
  });
  return (
    <div className={compact ? 'mode-selector mode-selector--compact chip-select' : 'mode-selector'}>
      {/* D-23: "full" runs everything without asking; it is said in plain words first */}
      {confirming ? (
        <ConfirmDialog
          title={t('agent.mode.fullTitle')}
          confirmLabel={t('agent.mode.fullOk')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={change.isPending}
          onConfirm={() => change.mutate('full')}
          onClose={() => setConfirming(false)}
          data-testid="mode-full-dialog"
        >
          {t('agent.mode.fullConfirm')}
        </ConfirmDialog>
      ) : null}
      <Select
        size={compact ? 'sm' : 'md'}
        aria-label={t('agent.mode.label')}
        title={compact ? t(`agent.mode.hints.${mode}`) : undefined}
        value={mode}
        disabled={change.isPending}
        onChange={(e) => {
          const next = e.target.value as ApprovalModeDto;
          if (next === 'full') setConfirming(true);
          else change.mutate(next);
        }}
        data-testid="mode-select"
      >
        {available.map((m) => (
          <option key={m} value={m}>
            {compact ? t('agent.mode.chip', { mode: t(`agent.mode.${m}`) }) : t(`agent.mode.${m}`)}
          </option>
        ))}
      </Select>
      {compact ? null : <span className="muted mode-selector__hint">{t(`agent.mode.hints.${mode}`)}</span>}
      <ErrorAlert error={change.error} />
    </div>
  );
}

export function NoticeLine({
  item,
  onContinue,
  continuing,
  onRetry,
  retrying,
}: {
  item: Extract<ChatItem, { kind: 'notice' }>;
  onContinue?: () => void;
  continuing?: boolean;
  /** QW-108: the same message again after a failed answer */
  onRetry?: () => void;
  retrying?: boolean;
}) {
  const { t, i18n } = useTranslation();
  let text: string;
  if (item.code === 'failed') {
    const key = `errors.${item.errorCode ?? ''}.title`;
    text = item.errorCode && i18n.exists(key) ? t(key) : t('agent.notice.failed');
  } else if (item.code === 'limit') {
    text = t(`agent.notice.limit.${item.limit?.kind ?? 'tool_calls'}`, { max: item.limit?.max ?? 0 });
  } else {
    text = t(`agent.notice.${item.code}`);
  }
  return (
    <div className={`message message--notice notice--${item.code}`} data-testid="notice" data-code={item.code}>
      <span>{text}</span>
      {onContinue ? (
        <Button size="sm" variant="secondary" busy={continuing} onClick={onContinue} data-testid="continue">
          {t('agent.continue')}
        </Button>
      ) : null}
      {onRetry ? (
        <Button size="sm" variant="secondary" busy={retrying} onClick={onRetry} data-testid="retry">
          {t('common.retry')}
        </Button>
      ) : null}
    </div>
  );
}

/** D-29: a card that waits while the tab is hidden raises a browser notification. */
export function useCardNotifications(pending: ApprovalView[], baseTitle: string) {
  const { t } = useTranslation();
  const seen = useRef(new Set<string>());
  useEffect(() => {
    for (const card of pending) {
      if (seen.current.has(card.id)) continue;
      seen.current.add(card.id);
      const fresh = Date.now() - new Date(card.createdAt).getTime() < 60_000;
      if (!fresh || !document.hidden || typeof Notification === 'undefined' || Notification.permission !== 'granted') continue;
      new Notification(card.kind === 'question' ? t('agent.notify.question') : t('agent.notify.approval'), { body: card.title, tag: card.id });
    }
    document.title = pending.length > 0 ? `(${pending.length}) ${baseTitle}` : baseTitle;
    return () => {
      document.title = baseTitle;
    };
  }, [pending, baseTitle, t]);
}
